import React, { ReactElement, useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import debounce from 'lodash/debounce'
import { Image } from '@alto-avios/alto-ui'
import { getStaticMap } from '~/services/googleService'
import { usePlatformTheme } from '~/context/theme'
import { useLocalisation } from '~/context/localisation'
import { useNotification } from '~/context/notification'
import { GenericClientError } from '~/utils/errors'
import { useFeatureFlag } from '~/context/featureFlag/FeatureFlagContext'
import { StaticMapProps } from './StaticMap.types'
import { getFittingZoom, serialisePins } from './StaticMap.utils'
import styles from './StaticMap.module.scss'

const DEFAULT_MARKER_COLOUR = '#3f51b5'
const AVIOS_MARKER_COLOUR = '#0032a0'
const AER_LINGUS_MARKER_COLOUR = '#006272'

const StaticMap = ({
  center,
  latitude,
  longitude,
  zoom,
  scale,
  language,
  maptype,
  format,
  displayMarker,
  displayPOI,
  pins,
  lockSize = false,
  forceLoad = false,
  objectFit = 'fill',
  alt = 'Static map',
}: StaticMapProps): ReactElement<React.PropsWithChildren> => {
  const { addErrorCodeNotification } = useNotification()
  const { locale } = useLocalisation()
  const { themeId } = usePlatformTheme()
  const staticMapEnabled = useFeatureFlag('static-map')
  const markerColor = useMemo(() => {
    switch (themeId) {
      case 'aer_lingus':
        return AER_LINGUS_MARKER_COLOUR
      case 'avios':
        return AVIOS_MARKER_COLOUR
      default:
        return DEFAULT_MARKER_COLOUR
    }
  }, [themeId])

  const [size, setSize] = useState([0, 0])

  // Pins carry their own framing, so the caller's zoom only applies when there is nothing to fit.
  const fittingZoom = useMemo(
    () => (pins ? getFittingZoom(pins, size[0], size[1]) : undefined),
    [pins, size]
  )

  const queryKey = useMemo(() => {
    return {
      center,
      latitude: latitude ?? pins?.[0]?.latitude,
      longitude: longitude ?? pins?.[0]?.longitude,
      pins: pins?.length ? serialisePins(pins) : undefined,
      width: size[0],
      height: size[1],
      markerColor,
      zoom: fittingZoom ?? zoom,
      scale,
      language: language ?? (locale === 'es-ES' ? 'es' : 'en'),
      maptype,
      format,
      displayMarker,
      displayPOI,
    }
  }, [
    center,
    latitude,
    longitude,
    pins,
    fittingZoom,
    size,
    markerColor,
    zoom,
    scale,
    language,
    locale,
    maptype,
    format,
    displayMarker,
    displayPOI,
  ])

  const enabled = useMemo(() => {
    const hasCoordinates = latitude != null && longitude != null
    const hasPosition = Boolean(center || hasCoordinates || pins?.length)
    return hasPosition && size[0] > 0 && size[1] > 0
  }, [center, latitude, longitude, pins, size])

  const { data, error, isError } = useQuery({
    queryKey: ['staticMap', queryKey],
    queryFn: () => getStaticMap(queryKey),
    enabled: enabled && (staticMapEnabled || forceLoad),
  })

  useEffect(() => {
    if (isError && error instanceof GenericClientError) {
      addErrorCodeNotification('STATIC_MAP_ERROR', {
        type: 'ERROR',
        errorCode: error.code,
        statusCode: error.httpStatus,
      })
    }
  }, [isError, error, addErrorCodeNotification])

  const observer = useRef<ResizeObserver | undefined>(undefined)

  /* istanbul ignore next */
  if (!observer.current && typeof ResizeObserver !== 'undefined') {
    observer.current = new ResizeObserver(
      debounce((entries: ResizeObserverEntry[]) => {
        entries
          .filter(({ contentRect }) => contentRect != null)
          .forEach((entry) => {
            const nextWidth = Math.round(entry.contentRect.width)
            const nextHeight = Math.round(entry.contentRect.height)
            setSize((currentSize) => {
              if (lockSize && currentSize[0] > 0 && currentSize[1] > 0) {
                return currentSize
              }

              const isEquivalentSize =
                currentSize[0] > 0 &&
                currentSize[1] > 0 &&
                Math.abs(currentSize[0] - nextWidth) <= 1 &&
                Math.abs(currentSize[1] - nextHeight) <= 1
              if (isEquivalentSize) {
                return currentSize
              }
              return [nextWidth, nextHeight]
            })
          })
      }, 300)
    )
  }
  const refCallback = useCallback((el: Element | null) => {
    if (observer.current) {
      if (el) {
        observer.current.observe(el)
      } else {
        observer.current.disconnect()
      }
    }
  }, [])

  return (
    <figure ref={refCallback} className={styles.wrapper}>
      {data?.map ? (
        <Image
          className={styles.imageWrapper}
          src={data.map}
          alt={alt}
          objectFit={objectFit}
          layout="fill"
          width={300}
          height={250}
          borderRadius="unset"
        />
      ) : null}
    </figure>
  )
}

export default StaticMap
